import React, { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { PluginModule, NavigationItem } from "../../plugins/types";
import { AdminProtectedRoute } from "../../foundation/altzero_base/base/components/admin/AdminProtectedRoute";

// Import pages from the pages directory (new structure)
import Dashboard from "./pages/Dashboard";
import Documents from "./pages/Documents";
import DocumentsGenerated from "./pages/DocumentsGenerated";
import DocumentsLibrary from "./pages/DocumentsLibrary";
import DocumentsNew from "./pages/DocumentsNew";
import DocumentsRegeneration from "./pages/DocumentsRegeneration";
import TemplateCreation from "./pages/TemplateCreation";
import KnowledgeBase from "./pages/KnowledgeBase";
import ScopingKnowledgeBase from "./pages/KnowledgeBaseDocuments";
import KnowledgeBasePrompts from "./pages/KnowledgeBasePrompts";
import Scoping from "./pages/Scoping";
import ScopingProposals from "./pages/ScopingProposals";
import NewProposal from "./pages/NewProposal";
import CreateFromExisting from "./pages/CreateFromExisting";
import QuickProposalPage from "./pages/QuickProposalPage";
import ProposalEditor from "./components/ProposalEditor";
import ClientsManagement from "./pages/ClientsManagement";
import ContactsManagement from "./pages/ContactsManagement";
import RetailersManagement from "./pages/RetailersManagement";
import SitesManagement from "./pages/SitesManagement";
import LOAManagement from "./pages/LOAManagement";
import TendersManagement from "./pages/TendersManagement";
import TenderDetails from "./pages/TenderDetails";
import Templates from "./pages/Templates";
import EmailManagement from "./pages/EmailManagement";
// Remove the old import since we moved the files

// Import the new reusable template editor
import TemplateEditor from "./components/TemplateEditor";
import TemplateEditorTest from "./test/TemplateEditorTest";
import PromptTemplateConfig from "./components/PromptTemplateConfig";
import { HTMLTemplateEditorPage } from "./templateEditor/HTMLTemplateEditorPage";

// Define routes for the tender plugin
const routes: RouteObject[] = [
  {
    path: "/tender",
    element: React.createElement(Dashboard),
  },
  {
    path: "/tender/dashboard",
    element: React.createElement(Dashboard),
  },
  {
    path: "/tender/documents",
    element: React.createElement(Documents),
  },
  {
    path: "/tender/documents/new",
    element: React.createElement(DocumentsNew),
  },
  {
    path: "/tender/documents/generated",
    element: React.createElement(DocumentsGenerated),
  },
  {
    path: "/tender/documents/regeneration",
    element: React.createElement(DocumentsRegeneration),
  },
  {
    path: "/tender/documents/library",
    element: React.createElement(DocumentsLibrary),
  },
  {
    path: "/tender/templates/create",
    element: React.createElement(TemplateCreation),
  },

  {
    path: "/tender/resource-library",
    element: React.createElement(KnowledgeBase),
  },

  {
    path: "/tender/resource-library/documents",
    element: React.createElement(DocumentsLibrary),
  },
  {
    path: "/tender/resource-library/clients",
    element: React.createElement(ClientsManagement),
  },
  {
    path: "/tender/resource-library/contacts",
    element: React.createElement(ContactsManagement),
  },
  {
    path: "/tender/resource-library/retailers",
    element: React.createElement(AdminProtectedRoute, { allowOrgAdmin: true }, React.createElement(RetailersManagement)),
  },
  {
    path: "/tender/resource-library/sites",
    element: React.createElement(SitesManagement),
  },
  {
    path: "/tender/resource-library/loas",
    element: React.createElement(LOAManagement),
  },
  {
    path: "/tender/resource-library/prompts",
    element: React.createElement(KnowledgeBasePrompts),
  },
  {
    path: "/tender/resource-library/scoping-knowledge-base",
    element: React.createElement(ScopingKnowledgeBase),
  },

  // Templates page - separate from Resource Library (Admin Only)
  {
    path: "/tender/templates",
    element: React.createElement(() =>
      React.createElement(AdminProtectedRoute, null,
        React.createElement(Templates)
      )
    ),
  },

  {
    path: "/tender/scoping",
    element: React.createElement(Scoping),
  },
  {
    path: "/tender/proposals",
    element: React.createElement(ScopingProposals),
  },
  {
    path: "/tender/resource-library/tenders",
    element: React.createElement(TendersManagement),
  },
  {
    path: "/tender/email",
    element: React.createElement(EmailManagement),
  },
  {
    path: "/tender/details/:id",
    element: React.createElement(TenderDetails),
  },
  {
    path: "/tender/retailer-results/:tenderId",
    element: React.createElement(lazy(() => import("./pages/RetailerResultsPage"))),
  },
  {
    path: "/tender/proposal/:id",
    element: React.createElement(lazy(() => import("./pages/TenderProposalViewer"))),
  },
  {
    path: "/tender/proposal-preview",
    element: React.createElement(lazy(() => import("./pages/ProposalPreview"))),
  },
  {
    path: "/tender/new-proposal",
    element: React.createElement(NewProposal),
  },
  {
    path: "/tender/quick-proposal",
    element: React.createElement(QuickProposalPage),
  },
  {
    path: "/tender/create-from-existing",
    element: React.createElement(CreateFromExisting),
  },
  {
    path: "/tender/proposal-editor",
    element: React.createElement(ProposalEditor),
  },
  {
    path: "/tender/proposal-viewer",
    element: React.createElement(lazy(() => import("./components/ProposalViewer"))),
  },
  {
    path: "/tender/template-editor",
    element: React.createElement(() =>
      React.createElement(AdminProtectedRoute, null,
        React.createElement(TemplateEditor)
      )
    ),
  },
  {
    path: "/tender/template-editor-test",
    element: React.createElement(() =>
      React.createElement(AdminProtectedRoute, null,
        React.createElement(TemplateEditorTest)
      )
    ),
  },
  {
    path: "/tender/prompt-template-config",
    element: React.createElement(() =>
      React.createElement(AdminProtectedRoute, null,
        React.createElement(PromptTemplateConfig)
      )
    ),
  },
  {
    path: "/tender/html-template-editor",
    element: React.createElement(() =>
      React.createElement(AdminProtectedRoute, null,
        React.createElement(HTMLTemplateEditorPage)
      )
    ),
  },
];

// Define navigation items - simple flat structure like pseo and crm
const navigation: NavigationItem[] = [
  {
    name: "Tender",
    route: "/tender",
    icon: "FileText",
    order: 7,
    permissions: ["tender:read"],
  },
  {
    name: "Sales",
    route: "/tender/quick-proposal",
    icon: "TrendingUp",
    order: 8,
    permissions: ["tender:read"],
  },
];

// tender plugin module
const tenderPlugin: PluginModule = {
  routes,
  navigation,
  providers: [], // No specific providers for tender yet
  config: {
    name: "tender",
    version: "1.0.0",
    description:
      "AI-powered project scoping and proposal generation system with document management and knowledge base",
  },
  initialize: async () => {
    console.log("🎯 tender plugin initialized");
  },
  cleanup: async () => {
    console.log("🎯 tender plugin cleaned up");
  },
};

export default tenderPlugin;

// Export the reusable Template Editor components for external use
export { default as TemplateEditor } from './components/TemplateEditor';
export type { TemplateEditorConfig, TemplateEditorProps } from './components/TemplateEditor';

// Export the core ModernTemplateEditor if needed
export { default as ModernTemplateEditor } from './templateEditor/components/ModernEditor/ModernTemplateEditor';

// Export services
export { ExportService } from './templateEditor/services/exportService';
export { default as GoogleFontsService } from './templateEditor/services/googleFontsService';
export { TemplateService } from './templateEditor/services/templateService';
export { ImportService } from './templateEditor/services/importService';

// Export utilities
export { default as ClipboardManager } from './templateEditor/utils/ClipboardManager';

// Export UI components that might be useful
// Note: StableInput and StableTextarea are imported directly where needed
export { TextFormattingWrapper } from './templateEditor/components/ui/TextFormattingWrapper';
export { AdvancedColorPicker } from './templateEditor/components/ui/AdvancedColorPicker';
export { GradientEditor } from './templateEditor/components/ui/GradientEditor';
export { FontSelector } from './templateEditor/components/ui/FontSelector';
export { PageBackgroundSidebar } from './templateEditor/components/ui/PageBackgroundSidebar';
